import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import type { Thresholds } from '../../core/thresholds';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type Confirmation = { at: string; by: string; byName?: string };

export type SetupState = {
  sisRequestedAt?: string;
  exitCriteriaConfirmed?: Confirmation;
  yearsConfirmed?: Confirmation;
};

export type Assessment = 'access' | 'alternate_access';
export type Outcome = 'exits' | 'eligible_for_review';
export type Measure =
  | 'overall'
  | 'literacy'
  | 'oral'
  | 'comprehension'
  | 'listening'
  | 'speaking'
  | 'reading'
  | 'writing';

export type Condition = { measure: Measure | string; minimum: number };
export type RuleSet = { outcome: Outcome; conditions: Condition[]; displayNote?: string };
export type GradeBand = { assessment: Assessment; grades: 'all' | string[]; noExit: boolean; ruleSets: RuleSet[] };
export type StateRecordVersion = {
  effectiveFrom: string;
  sourceUrl: string;
  verifiedOn: string;
  confidence: string;
  gradeBands: GradeBand[];
};
export type StateRecord = { state: string; abbr: string; versions: StateRecordVersion[] };

export type EffectiveCriteria = { source: 'state_record' | 'district'; effectiveFrom: string; gradeBands: GradeBand[] };

export type DistrictCriteriaSummary = { basedOn: { state: string; stateRecordVersion: string } };

export type DifferenceKind = 'changed' | 'added' | 'rule_added' | 'removed';
export type DifferenceStatus = 'open' | 'state_record_corrected' | 'district_specific';
export type CriteriaDifference = {
  assessment: Assessment;
  grades: 'all' | string[];
  outcome: Outcome;
  measure: string;
  kind: DifferenceKind;
  recordMinimum?: number;
  districtMinimum?: number;
};
export type CriteriaDifferences = {
  stateRecordVersion: string;
  items: CriteriaDifference[];
  savedBy: string;
  savedAt: string;
  status: DifferenceStatus;
  resolvedBy?: string;
  resolvedAt?: string;
};

export type ExitCriteriaSettings = {
  state: string;
  stateName?: string;
  schoolYear: string;
  followsStateRecord: boolean;
  stateRecord?: StateRecord;
  districtCriteria?: DistrictCriteriaSummary;
  effective?: EffectiveCriteria;
  exitCriteriaConfirmed?: Confirmation;
  yearsConfirmed?: Confirmation;
  changedAt?: string;
  differences?: CriteriaDifferences;
};

export type YearsSettings = Thresholds & { stored: boolean; changedAt?: string; yearsConfirmed?: Confirmation };

export type PlanSignOffRule = 'el_teacher_only' | 'every_teacher';
export type PlanListItem = { id: string; label: string; matchesClassroomItem?: string; requiresIepOr504?: boolean };
export type PlanLists = { classroom: PlanListItem[]; contentTests: PlanListItem[]; access: PlanListItem[] };
export type PlanSettings = { lists: PlanLists; signOffRule: PlanSignOffRule; stored: boolean; updatedAt?: string; updatedBy?: string };

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    throw new Error(`${init.method ?? 'GET'} ${path} failed: ${response.status}`);
  }
  return response.json();
}

export function fetchExitCriteria(): Promise<ExitCriteriaSettings> {
  return request('/settings/exit-criteria') as Promise<ExitCriteriaSettings>;
}


export class CriteriaRecordChangedError extends Error {
  current: ExitCriteriaSettings;

  constructor(current: ExitCriteriaSettings) {
    super('the state record changed since the criteria were read');
    this.name = 'CriteriaRecordChangedError';
    this.current = current;
  }
}

export async function saveExitCriteria(gradeBands: GradeBand[], reviewed: ReviewedConfirmation): Promise<ExitCriteriaSettings> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/exit-criteria`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ gradeBands, ...reviewed }),
  });
  if (response.status === 409) throw new CriteriaRecordChangedError((await response.json()) as ExitCriteriaSettings);
  if (!response.ok) throw new Error(`PUT /settings/exit-criteria failed: ${response.status}`);
  return (await response.json()) as ExitCriteriaSettings;
}

export type ReviewedCriteria = { schoolYear: string; stateRecordVersion: string; verifiedOn: string };
export type ReviewedConfirmation = ReviewedCriteria & { changedAt?: string };

export async function confirmExitCriteria(reviewed: ReviewedConfirmation): Promise<SetupState> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/exit-criteria/confirm`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(reviewed),
  });
  if (response.status === 409) throw new CriteriaRecordChangedError((await response.json()) as ExitCriteriaSettings);
  if (!response.ok) throw new Error(`POST /settings/exit-criteria/confirm failed: ${response.status}`);
  return (await response.json()) as SetupState;
}

export class YearsChangedError extends Error {
  current: YearsSettings;

  constructor(current: YearsSettings) {
    super('years changed since they were read');
    this.name = 'YearsChangedError';
    this.current = current;
  }
}

export async function confirmYears(reviewed: Thresholds): Promise<YearsSettings> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/years/confirm`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(reviewed),
  });
  if (response.status === 409) throw new YearsChangedError(asYears(await response.json()));
  if (!response.ok) throw new Error(`POST /settings/years/confirm failed: ${response.status}`);
  return asYears(await response.json());
}

function asYears(body: unknown): YearsSettings {
  const t = body as Record<string, unknown> | null;
  if (
    !t ||
    typeof t.newcomerMaxYears !== 'number' ||
    typeof t.ltelMinYears !== 'number' ||
    typeof t.monitoringYears !== 'number'
  ) {
    throw new Error('generation-settings response is missing a value');
  }
  const out: YearsSettings = {
    newcomerMaxYears: t.newcomerMaxYears,
    ltelMinYears: t.ltelMinYears,
    monitoringYears: t.monitoringYears,
    stored: t.stored === true,
  };
  if (typeof t.changedAt === 'string') out.changedAt = t.changedAt;
  const confirmed = t.yearsConfirmed as Record<string, unknown> | undefined;
  if (confirmed && typeof confirmed.at === 'string' && typeof confirmed.by === 'string') {
    out.yearsConfirmed = { at: confirmed.at, by: confirmed.by };
    if (typeof confirmed.byName === 'string') out.yearsConfirmed.byName = confirmed.byName;
  }
  return out;
}

function asPlanSettings(body: unknown): PlanSettings {
  const t = body as Record<string, unknown> | null;
  const lists = t?.lists as Record<string, unknown> | undefined;
  if (!t || !lists || !Array.isArray(lists.classroom) || !Array.isArray(lists.contentTests) || !Array.isArray(lists.access)) {
    throw new Error('plan settings response is missing its lists');
  }
  const out: PlanSettings = {
    lists: { classroom: lists.classroom as PlanListItem[], contentTests: lists.contentTests as PlanListItem[], access: lists.access as PlanListItem[] },
    signOffRule: t.signOffRule === 'every_teacher' ? 'every_teacher' : 'el_teacher_only',
    stored: t.stored === true,
  };
  if (typeof t.updatedAt === 'string') out.updatedAt = t.updatedAt;
  if (typeof t.updatedBy === 'string') out.updatedBy = t.updatedBy;
  return out;
}

export async function fetchPlanSettings(): Promise<PlanSettings> {
  return asPlanSettings(await request('/settings/plan'));
}

export async function savePlanSignOffRule(rule: PlanSignOffRule): Promise<PlanSettings> {
  return asPlanSettings(
    await request('/settings/plan/sign-off-rule', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rule }),
    })
  );
}

export async function fetchYears(): Promise<YearsSettings> {
  return asYears(await request('/settings/generation'));
}

export async function saveYears(values: Thresholds): Promise<YearsSettings> {
  return asYears(
    await request('/settings/generation', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    })
  );
}
